import { executeTool } from '../utils/agentTools';
import { post } from './api';

// Message interface for conversation history
export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatResponse {
  type: 'text' | 'function_calls';
  message?: string;
  functionCalls?: Array<{
    name: string;
    args: Record<string, unknown>;
  }>;
}

/**
 * Send a message to the backend (which calls Vertex AI)
 * Handles function calling in a loop
 */
export async function sendMessage(
  userMessage: string,
  conversationHistory: Array<{ role: string; content: string }>
): Promise<{ response: string; updatedHistory: Array<{ role: string; content: string }> }> {

  try {
    // Add user message to history first
    const updatedHistory = [
      ...conversationHistory,
      { role: 'user', content: userMessage },
    ];

    let currentMessage = userMessage;
    let toolResults: unknown = null;
    let iterations = 0;
    const maxIterations = 10;

    // Loop to handle function calling
    while (iterations < maxIterations) {
      iterations++;
      console.log(`Iteration ${iterations}: Sending to backend`);

      // Call backend with authentication
      const data = await post<ChatResponse>('/api/chat', {
        message: toolResults ? undefined : currentMessage,
        history: conversationHistory,
        toolResults: toolResults,
      });

      console.log('Backend response:', data);

      if (data.type === 'function_calls' && data.functionCalls) {
        // Execute function calls locally
        console.log('Executing function calls:', data.functionCalls);

        toolResults = data.functionCalls.map((fc) => {
          const result = executeTool(fc.name, fc.args);
          console.log(`Function ${fc.name} result:`, result);

          return {
            functionResponse: {
              name: fc.name,
              response: result,
            },
          };
        });

        // Continue loop to send results back
        continue;
      }

      if (data.type === 'text') {
        // Got final text response
        const finalHistory = [
          ...updatedHistory,
          { role: 'assistant', content: data.message || '' },
        ];

        return {
          response: data.message || '',
          updatedHistory: finalHistory,
        };
      }

      throw new Error('Unexpected response type from backend');
    }

    throw new Error('Max iterations reached in function calling loop');

  } catch (error) {
    console.error('Error in vertex agent:', error);

    // Handle specific error types
    let errorMessage = 'I apologize, I encountered an unexpected error. Please try again.';

    if (error instanceof Error) {
      // Handle auth errors
      if ((error as any).code === 'AUTH_ERROR' || error.message.includes('Authentication required')) {
        errorMessage = `⚠️ **Authentication Required**

You need to sign in to use the AI assistant.

Please refresh the page and sign in with your BankingLATAM account.`;
      } else if ((error as any).code === 'ACCESS_DENIED' || error.message.includes('Access denied')) {
        errorMessage = `⚠️ **Access Denied**

You don't have permission to use this feature.

Please contact your administrator if you believe this is an error.`;
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = `⚠️ **Cannot Connect to Backend**

The backend server is not running or not accessible.

**To fix this:**
1. Open a new terminal
2. Run: \`cd backend && npm start\`
3. Make sure it starts on http://localhost:3001
4. Try your message again`;
      } else if (error.message.includes('Vertex AI API') || error.message.includes('permission')) {
        errorMessage = `⚠️ **Vertex AI API Issue**

${error.message}

**To fix this:**
1. Enable Vertex AI API: https://console.cloud.google.com/apis/library/aiplatform.googleapis.com
2. Or run: \`gcloud services enable aiplatform.googleapis.com\`
3. Restart the backend server`;
      } else if (error.message.includes('credentials')) {
        errorMessage = `⚠️ **Google Cloud Authentication Issue**

${error.message}

**To fix this:**
1. Run: \`gcloud auth application-default login\`
2. Or set GOOGLE_APPLICATION_CREDENTIALS environment variable
3. Restart the backend server`;
      } else {
        errorMessage = `I apologize, I encountered an error: ${error.message}

Please check the browser console and backend logs for more details.`;
      }
    }

    const errorHistory = [
      ...conversationHistory,
      { role: 'user', content: userMessage },
      { role: 'assistant', content: errorMessage },
    ];

    return {
      response: errorMessage,
      updatedHistory: errorHistory,
    };
  }
}
